import React, { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import { useFrame } from 'react-frame-component';
import { Message } from '@outdoorly/chat-sdk';

export const useMeasuredSize = (
  ref: HTMLElement | null,
  callback: (size?: { width: number; height: number }) => void
) => {
  const [size, setSize] = useState<{ height: number; width: number }>();

  useLayoutEffect(() => {
    setSize(ref ? { width: ref.scrollWidth, height: ref.scrollHeight } : undefined);
  }, [ref]);

  useResizeObserver(ref, (entry) =>
    setSize({ width: entry.target.scrollWidth, height: entry.target.scrollHeight })
  );
  useEffect(() => callback(size), [size, callback]);
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

export const useMemoizedFrame = () => {
  const { window, document } = useFrame();
  const [state, setState] = useState({ window, document });

  useEffect(() => {
    if (window !== state.window || document !== state.document) {
      setState({ window, document });
    }
  });

  return state;
};
